import React from 'react';

// Material UI
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';

// Components
import { SectionWrapper, Spacer } from '../../_core/_ui/structure.components';
import ManageItemsListContainer from '../../_core/ManageItemsList/manage-items-list.container';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { IFinalizeSessionFields } from './approve-groupings.interface';
import { numToTime, toWeighted } from '../../../lib/helpers/conversion.helpers';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from './approve-groupings.messages';
import { Link } from 'react-router-dom';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SportTerminology } from '../../../lib/types';
import { getPlayerName } from '../../../lib/utils/player-name.service';

interface IApproveGroupings {
  sessionSets: IFinalizeSessionFields[];
  plans: IAssessmentStagePlan[];
  playerAssessments: IPlayerAssessment[];
  playerAssessmentsById: { [key: string]: IPlayerAssessment };
  drills: IDrill[];
  practicePlans: IPracticePlan[];
  onSubmit: Function;
  isSubmitting: boolean;
  submitError: React.ReactNode;
  loading: boolean;
  fetchError: boolean;
  fetchErrorMessage?: string | null;
  removePlayerAssessment: Function;
  removeDrill: Function;
  removePracticePlan: Function;
  addPlayerAssessment: Function;
  addDrill: Function;
  addPracticePlan: Function;
  hasSubmitted: boolean
}


const ApproveGroupings: React.FC<IApproveGroupings> = ({
  sessionSets,
  plans,
  playerAssessments,
  playerAssessmentsById,
  drills,
  practicePlans,
  onSubmit,
  isSubmitting,
  submitError,
  loading,
  fetchError,
  fetchErrorMessage,
  removePlayerAssessment,
  removeDrill,
  removePracticePlan,
  addPlayerAssessment,
  addDrill,
  addPracticePlan,
  hasSubmitted
}) => {
  const termLookup = useCompanySportTerminology()
  const athletesTerm = pluralTerm(termLookup[SportTerminology.ATHLETE])
  const sessionTerm = termLookup[SportTerminology.SESSION]

  if (loading && !plans.length) return <Typography>Loading...</Typography>;
  if (fetchError && !plans.length) return <Typography>{fetchErrorMessage || <IntlMsg {...messages.fetchFailed} />}</Typography>;
  if (hasSubmitted) return <Typography>{athletesTerm} have been scheduled with the configured {pluralTerm(termLookup[SportTerminology.SESSION]).toLowerCase()}</Typography>;
  let sessionIndex = 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box mt={2} display="flex" alignItems="center">
          <Typography>The schedule below has been generated based on the <Link to="/format">schedule format</Link></Typography>
          <Spacer />
          <PrimaryButton disabled={hasSubmitted || isSubmitting} onClick={() => onSubmit(sessionSets)}>Finalize Groups and Trigger Notifications</PrimaryButton>
        </Box>
        {submitError && <Box mt={2}><Alert severity="error">{submitError}</Alert></Box>}
      </Grid>
      {plans.map((plan) => {
        return (
          <Grid item xs={12} key={plan.sessionStage._id}>
            <SectionWrapper dark>
              {/* Stages are optionally titled, so an untitled one falls back to the plain term. */}
              <Typography variant="h6" gutterBottom>{plan.sessionStage.title || sessionTerm}</Typography>
              <Grid container spacing={2}>
                {plan.sessionSets.map((sessionSet, setIndex) => {
                  const { session } = sessionSet;
                  const currSessionSet = sessionSets[sessionIndex];
                  const currSessionIndex = sessionIndex;
                  sessionIndex += 1;

                  if (!currSessionSet) return (
                    <Grid item xs={12} md={4}>
                      <SectionWrapper>
                        <Typography>Loading...</Typography>
                      </SectionWrapper>
                    </Grid>
                  );

                  return (
                    <Grid item xs={12} md={4} key={`${plan.sessionStage._id}_${setIndex}`}>
                      <SectionWrapper>
                        <Typography variant="subtitle1">{session.location} - {session.area}</Typography>
                        <Typography variant="subtitle1" gutterBottom>{session.date} @ {numToTime(session.start)}</Typography>

                        <Box mb={2}><Divider /></Box>

                        <Typography variant="subtitle2" gutterBottom>Practice Plans</Typography>
                        <ManageItemsListContainer
                          entity="practice plan"
                          options={practicePlans}
                          selectedItems={currSessionSet.practicePlans || []}
                          addItem={(item: IPracticePlan) => addPracticePlan(item, currSessionIndex)}
                        >
                          {(practicePlan: IPracticePlan) => (
                            <ListItem key={practicePlan._id}>
                              <ListItemText primary={practicePlan.name} />
                              <ListItemSecondaryAction>
                                <IconButton edge="end" aria-label="delete" onClick={() => removePracticePlan(practicePlan, currSessionIndex)}>
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          )}
                        </ManageItemsListContainer>

                        <Box mb={2}><Divider /></Box>

                        <Typography variant="subtitle2" gutterBottom>{pluralTerm(termLookup[SportTerminology.EVENT])}</Typography>
                        <ManageItemsListContainer
                          entity="drill"
                          options={drills}
                          selectedItems={currSessionSet.drills || []}
                          addItem={(item: IDrill) => addDrill(item, currSessionIndex)}
                        >
                          {(drill: IDrill) => (
                            <ListItem key={drill._id}>
                              <ListItemText primary={`${drill.name}`} />
                              <ListItemSecondaryAction>
                                <IconButton edge="end" aria-label="delete" onClick={() => removeDrill(drill, currSessionIndex)}>
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          )}
                        </ManageItemsListContainer>

                        <Box mb={2}><Divider /></Box>

                        <Typography variant="subtitle2" gutterBottom>{athletesTerm}</Typography>
                        <ManageItemsListContainer
                          entity="player"
                          options={playerAssessments}
                          itemLabel={(item: IPlayerAssessment) => `${getPlayerName({player: item.player})}${item.isHeld ? ' - On Hold' : ''}${item.isConcluded ? ' - Concluded' : ''}`}
                          selectedItems={currSessionSet.playerAssessments || []}
                          addItem={(item: IPlayerAssessment) => addPlayerAssessment(item, currSessionIndex)}
                        >
                          {(playerAssessment: IPlayerAssessment) => {
                            const currPlayerAssessment = playerAssessmentsById[playerAssessment._id];

                            return (
                              <ListItem key={playerAssessment._id}>
                                <ListItemText
                                  primary={<Box display="flex" pr={2}>
                                    <Typography>{getPlayerName(playerAssessment)}</Typography>
                                    <Spacer/>
                                    <Typography>{toWeighted(currPlayerAssessment?.adjustedScore)}</Typography>
                                  </Box>}
                                  secondary={playerAssessment.position.name}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton edge="end" aria-label="delete"
                                              onClick={() => removePlayerAssessment(playerAssessment, currSessionIndex)}>
                                    <DeleteIcon/>
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            );
                          }}
                        </ManageItemsListContainer>
                      </SectionWrapper>
                    </Grid>
                  );
                })}
              </Grid>
            </SectionWrapper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default ApproveGroupings;
